import React, { useEffect, useMemo, useState } from 'react'

import {
  AlignItems,
  Box,
  ButtonColorVariant,
  ButtonSize,
  Display,
  Heading3,
  Heading6,
  Icon,
  Modal,
  ModalBody,
  ModalButton,
  ModalClose,
  ModalFooter,
  ModalHeader,
  Space,
  useModal,
} from '@gousto-internal/citrus-react'
import classnames from 'classnames'

import { DayV2 } from '@library/api-deliveries'

import { trackSubscriptionSettingsChange } from '../../tracking'
import { CalendarDeliverDay, useSubscriptionCalendar } from '../hooks/useSubscriptionCalendar'
import { dateFormatter } from '../utils/dateFormatter'

import css from './Calendar.css'

export const SUBSCRIPTION_CALENDAR_MODAL_NAME = 'SUBSCRIPTION-CALENDAR-MODAL'

type Props = {
  deliveryDays: DayV2[] | null
  preSelectedDay: string | null
  updateSelectedDayFromCalendar: (day: CalendarDeliverDay) => void
  showModal: boolean
}

export const CalendarModal = ({
  deliveryDays,
  preSelectedDay,
  updateSelectedDayFromCalendar,
  showModal,
}: Props) => {
  const dateFormatterModule = dateFormatter()
  const initialSelectedDay = useMemo(() => {
    const matchedDateStringToDayV2 = deliveryDays?.find((day) => day.date === preSelectedDay)
    if (!matchedDateStringToDayV2) {
      return null
    }

    return {
      id: matchedDateStringToDayV2.id,
      slotId: matchedDateStringToDayV2.daySlotLeadTimes[0].slotId,
      date: matchedDateStringToDayV2.date,
      defaultDay: matchedDateStringToDayV2.daySlotLeadTimes[0].defaultDay,
    }
  }, [deliveryDays, preSelectedDay])
  const [selectedDay, setSelectedDay] = useState<CalendarDeliverDay | null>(initialSelectedDay)
  const { getCalendarWeekDays, getCalendarDays, getInitialDate, getLastDate } =
    useSubscriptionCalendar(deliveryDays)
  const { closeCurrentModal } = useModal()

  const initialDate = getInitialDate()
  const lastDate = getLastDate()

  const initialFormattedDate = dateFormatterModule.getDayOfMonth(initialDate)
  const lastFormattedDate = dateFormatterModule.getDayOfMonth(lastDate)

  const initialMonth =
    new Date(initialDate).getMonth() === new Date(lastDate).getMonth()
      ? ''
      : ` ${dateFormatterModule.getShortMonthName(initialDate)}`

  useEffect(() => {
    if (preSelectedDay) {
      setSelectedDay(initialSelectedDay)
    }
  }, [preSelectedDay, initialSelectedDay])

  const handleDayClick = (day: CalendarDeliverDay) => () => {
    setSelectedDay(day)
  }

  const handleSaveClick = () => {
    if (!selectedDay) {
      return
    }

    updateSelectedDayFromCalendar(selectedDay)
    trackSubscriptionSettingsChange({
      settingName: 'reactivate_delivery_date',
      action: 'update',
    })({
      previous_date: preSelectedDay,
      updated_date: selectedDay?.date,
    })
    closeCurrentModal()
  }

  return (
    <Modal isOpen={showModal} name={SUBSCRIPTION_CALENDAR_MODAL_NAME} onAfterClose={() => {}}>
      <ModalClose onClose={closeCurrentModal} />
      <ModalHeader>
        <Heading3>Choose next delivery date</Heading3>
      </ModalHeader>

      <ModalBody>
        <Box paddingBottom={3} display={Display.Flex} alignItems={AlignItems.Center}>
          <Icon name="calendar" />
          <Space direction="horizontal" size={2} />
          <Heading6>{`${initialFormattedDate}${initialMonth} - ${lastFormattedDate} ${dateFormatterModule.getShortMonthName(
            lastDate,
          )}`}</Heading6>
        </Box>
        <table className={css.calendarTable}>
          <thead className={css.calendarTableHead}>
            <tr>
              {getCalendarWeekDays().map((weekDay, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <th className={css.headerCell} key={`${weekDay}${index}`}>
                  {weekDay}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {getCalendarDays().map((week) => (
              // eslint-disable-next-line react/jsx-key
              <tr>
                {week.map((day, index) =>
                  !day ? (
                    // eslint-disable-next-line react/no-array-index-key
                    <td className={css.tableCell} key={`empty-day-${index}`} />
                  ) : (
                    <td className={css.tableCell} key={day.id}>
                      <div className={css.squareContainer}>
                        <button
                          type="button"
                          disabled={day.disabled}
                          onClick={handleDayClick(day)}
                          className={classnames(css.day, {
                            [css.selectedDay]: selectedDay?.id === day.id,
                          })}
                        >
                          <span className={css.dayNumber}>
                            {dateFormatterModule.getDayOfMonth(day.date)}
                          </span>
                        </button>
                      </div>
                    </td>
                  ),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </ModalBody>

      <ModalFooter>
        <ModalButton
          colorVariant={ButtonColorVariant.Primary}
          size={ButtonSize.Large}
          onClick={handleSaveClick}
        >
          {`Select ${dateFormatterModule.getWeekDayName(
            selectedDay?.date,
          )} (starting ${dateFormatterModule.getFormattedMonthDayWithoutWeekday(
            selectedDay?.date,
          )})`}
        </ModalButton>
      </ModalFooter>
    </Modal>
  )
}
